import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { useFavorites } from "../lib/useFavorites";
import { Page, RichLoader, ErrorBox } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { ElectionCard } from "../components/ElectionCard";
import { flag } from "../lib/flag";
import { useT } from "../i18n";

interface FavElection {
  id: string;
  countryId: string;
  countryName: string;
  countryCode: string;
  type: string;
}

export default function Country() {
  const t = useT();
  const { countryId = "" } = useParams();
  const nav = useNavigate();
  const favE = useFavorites<FavElection>("elections");
  const { data, loading, error } = useAsync(() => api.countryElections(countryId), [countryId]);
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);

  return (
    <Page>
      <Link to="/world" className="text-accent text-sm">← {t("country.allCountries")}</Link>
      {showRich && <RichLoader headline={t("common.loadingElections")} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}
      {data && data.length === 0 && <p className="text-muted text-sm mt-4">{t("country.noElections")}</p>}

      {data && data[0] && (
        <h1 className="text-xl font-semibold my-3">
          {flag(data[0].countryCode)} {data[0].countryName}
        </h1>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3 mt-2">
        {data?.map((e) => (
          <ElectionCard
            key={e.id || `sched-${e.type}`}
            e={e}
            fav={e.scheduled ? false : favE.isFav(e.id)}
            onFav={() => {
              if (!e.scheduled) favE.toggle({ id: e.id, countryId, countryName: e.countryName, countryCode: e.countryCode, type: e.type });
            }}
            onOpen={() => {
              if (!e.scheduled) nav(`/c/${countryId}/e/${e.id}`);
            }}
            // Reachable in every phase — not just scheduled — so the candidacy hub
            // doesn't get orphaned once an election goes live. Pass the phase so the
            // race page shows accurate copy.
            onOpenRace={() => nav(`/c/${countryId}/race/${e.type}`, { state: { phase: e.phase } })}
          />
        ))}
      </div>
    </Page>
  );
}
